import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PresignedUrlItem {
    fileId: string;
    presignedUrl: string;
    s3Key: string;
}

export interface InitPresignedResponse {
    urls: PresignedUrlItem[];
}

export interface ConfirmRecord {
    s3Key: string;
    activity: string;
    facility: string;
    recordDate: string;
}

@Injectable({
    providedIn: 'root'
})
export class UploadFileService {

    public BACKEND_HOST = environment.url;

    private http = inject(HttpClient);

    /** PUT file directly to a presigned S3 URL. Rejects on non-2xx or network error. */
    public putToPresignedUrl(file: File, presignedUrl: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', presignedUrl);
            xhr.setRequestHeader('Content-Type', 'application/pdf');
            xhr.withCredentials = false;
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                } else {
                    reject(new Error(`Upload failed: HTTP ${xhr.status}`));
                }
            };
            xhr.onerror = () => reject(new Error('Network error'));
            xhr.ontimeout = () => reject(new Error('Timeout'));
            xhr.timeout = 5 * 60 * 1000;
            xhr.send(file);
        });
    }

    /** Get presigned PUT URLs. folder optional (e.g. userId for add-patient). */
    public initPresignedUpload(
        folder: string | null,
        files: { fileId: string; name: string; size: number }[]
    ): Observable<{ data: InitPresignedResponse }> {
        return this.http.post<{ data: InitPresignedResponse }>(
            `${this.BACKEND_HOST}/uploads/init`,
            { folder, files },
            { withCredentials: true }
        );
    }

    /** Create MedicalRecord entries after client has uploaded files via presigned PUT. */
    public confirmPresignedUploads(userId: string, records: ConfirmRecord[]): Observable<{ data: { created: number } }> {
        return this.http.post<{ data: { created: number } }>(
            `${this.BACKEND_HOST}/uploads/confirm`,
            { userId, records },
            { withCredentials: true }
        );
    }

    public upload(formData: any, folder: any, bucket: any): any {
            return this.http.post<any>(`${this.BACKEND_HOST}/s3/upload`, formData, {
                params: {
                    folder,
                    bucket
                },
                reportProgress: true,
                observe: 'events'
            });
    }

    /** Upload medical record with metadata (activity, facility, recordDate). Uses POST /s3/user-upload */
    public userUpload(file: File, folder: string, bucket: string, body: { activity: string; facility: string; recordDate: string }): any {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('activity', body.activity);
        formData.append('facility', body.facility);
        formData.append('recordDate', body.recordDate);
        return this.http.post<any>(`${this.BACKEND_HOST}/s3/user-upload`, formData, {
            params: { folder, bucket },
            withCredentials: true,
        });
    }

    public uploadAlt(formData: any, folder: any, bucket: any): any {

            return this.http.post<any>(`${this.BACKEND_HOST}/s3/upload-alt`, formData, {
                params: {
                    folder,
                    bucket
                },
                reportProgress: true,
                observe: 'events'
            });
        
    }

    public nextGenUpload(formData: any, password: any): any {
            return this.http.post<any>(`${this.BACKEND_HOST}/next-gen-user/upload`, formData, {
                params: {
                    password,
                },
            });
    }

    public viewFile2(fileDir: any): any {
        const [folderId, filename] = fileDir.split('/');

            return this.http.get<any>(`${this.BACKEND_HOST}/s3/get-file`, {
                responseType: 'blob' as 'json',
                params: {
                    folderId,
                    filename
                },
                reportProgress: true,
            });
        
    }

    viewFile(fileDir: any): any {
        const [folderId, filename] = fileDir.split('/');
        return this.http.get(`${this.BACKEND_HOST}/s3/get-file/${filename}`, {
            params: {
                folderId,
                filename
            },
            responseType: 'blob', 
            observe: 'response'
        }).pipe(
            map((res: any) => {
                const blob = filename.substr(filename.length - 3) === 'pdf' ?
                    new Blob([res.body], { type: 'application/pdf' }) :
                    new Blob([res.body], { type: 'image/png' });
                return blob;
            })
        );
    }

    _base64ToArrayBuffer(base64: any): any {
        const binaryString = window.atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    }

}
